import TitleBar from '../../../components/TitleBar';
import { ColumnsType } from 'antd/es/table';
import { Footer } from '../../../components/common/Footer';
import { Button } from '../../../components';
import { DependencyMatrix, Products } from './DependencyMatrix';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { FETCH_LEVEL_DEPENDENCY_MATRIX } from '../queries';
import { LOCATION_TYPES } from '../../../constants';
import FinancialData from '../../../components/common/FinancialData';
import TableV3 from '../../../components/generic/Table/TableV3';
import {
  getLevelLocationData,
  tableSortingMethods,
  updatePreviousLocations,
} from '../../../utils';
import QueryErrorBoundary from '../../../components/error-handling';
import { ExportColumn } from '../../../interfaces/ExportColumn';
import TitleUpdater from '../../../components/common/TitleUpdater';

interface TableData {
  location: string;
  percentage: number;
  supportedRevenue: number;
  locationId: string;
  locationType: string;
}

const convertData = (data: Products): TableData[] => {
  return data.locationAuxs?.map((location) => {
    return {
      location: location.name,
      percentage: location.percentage,
      supportedRevenue: location.supportedRevenue,
      locationId: location.key,
      locationType: location.locationType,
    };
  });
};

function LevelSummary() {
  const navigate = useNavigate();
  const params = useParams();
  let [searchParams] = useSearchParams();
  const divisionId = params.divisionId!;
  const parentLocation = params.parentLocation!;
  const level = parseInt(searchParams.get('level')!);
  const locationId = searchParams.get('locationId')!;
  const divisionName = searchParams.get('divisionName') ?? '';
  const locationName = searchParams.get('locationName') ?? '';
  const prevLocationId = searchParams.get('prevLocationId') || '';
  const prevLocationName = searchParams.get('prevLocationName') || '';

  const {
    prevLocationId: updatedPrevLocationId,
    prevLocationName: updatedPrevLocationName,
  } = updatePreviousLocations(
    locationId,
    locationName,
    prevLocationId,
    prevLocationName,
  );

  const { locationId: levelLocationId, locationName: levelLocationName } =
    getLevelLocationData(updatedPrevLocationId, updatedPrevLocationName, level);

  const { data, loading, error } = useQuery<DependencyMatrix>(
    FETCH_LEVEL_DEPENDENCY_MATRIX,
    {
      variables: {
        division: { uuid: divisionId },
        level,
        location: { uuid: levelLocationId },
        parentLocation: { uuid: parentLocation },
      },
      skip: !divisionId || !levelLocationId || !parentLocation,
    },
  );

  const columns: ColumnsType<TableData> = [
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      sorter: (a, b) => tableSortingMethods.string(a.location, b.location),

      render: (text: string) => (
        <div className="flex flex-col">
          <span className="custom-table--column">{text}</span>
        </div>
      ),
    },
    {
      title: '% Of Division Supported',
      dataIndex: 'percentage',
      key: 'percentage',
      align: 'right',
      sorter: (a, b) => tableSortingMethods.number(a.percentage, b.percentage),

      render: (text: string) => (
        <span className="custom-table--column">{text} %</span>
      ),
    },
    {
      title: 'Division Revenue Supported',
      dataIndex: 'supportedRevenue',
      key: 'supportedRevenue',
      align: 'right',
      sorter: (a, b) =>
        tableSortingMethods.number(a.supportedRevenue, b.supportedRevenue),
      render: (text: number) => (
        <span className="custom-table--column">
          {<FinancialData text={text} />}
        </span>
      ),
    },
    {
      title: '',
      key: 'action',
      render: (_, record) =>
        record?.locationType !== LOCATION_TYPES.DISTRIBUTION_CENTER && (
          <Button
            type="primary-rounded"
            className="text-sm"
            onClick={() =>
              navigate(
                `/dependency-mapping/${divisionId}/${parentLocation}/level-locations?locationId=${
                  record.locationId
                }&level=${
                  level + 1
                }&divisionName=${divisionName}&locationName=${
                  record.location
                }&prevLocationId=${updatedPrevLocationId}&prevLocationName=${updatedPrevLocationName}`,
              )
            }
          >
            Map Level {level + 1}
          </Button>
        ),
    },
  ];

  const baseColumns: ExportColumn[] = [
    { key: 'location', header: 'Location', type: 'text', width: 45 },
    {
      key: 'percentage',
      header: '% Of Division Supported',
      type: 'percentage',
      width: 40,
    },
    {
      key: 'supportedRevenue',
      header: 'Division Revenue Supported',
      type: 'money',
      width: 40,
    },
  ];

  return (
    <>
      <TitleUpdater title={`Level ${level} Summary`} />
      <TitleBar
        title="Dependency Mapping"
        subtitle={`Level ${level} Mapping // ${divisionName} // ${levelLocationName}`}
        brief={`Level ${level} dependency mapping is focused on locations that directly support locations that make intermediates. Below is 
        the list of locations that support intermediates at level ${level}. Click on any location to capture the level
         ${level} dependencies. Note that only those locations that exist at level ${level} will appear here.`}
      />
      <QueryErrorBoundary error={error}>
        <TableV3
          title={`Level ${level} Summary`}
          columns={columns}
          data={
            data?.findDivisionDependencyMatrix?.division
              ? convertData(data?.findDivisionDependencyMatrix?.division)
              : []
          }
          loading={loading}
          exportExcelColumns={baseColumns}
        />
      </QueryErrorBoundary>
      <Footer
        backPath={`/dependency-mapping/${divisionId}/${parentLocation}/level-mapping?level=${level}&locationId=${locationId}&divisionName=${divisionName}&locationName=${locationName}&prevLocationId=${updatedPrevLocationId}&prevLocationName=${updatedPrevLocationName}`}
      />
    </>
  );
}

export default LevelSummary;
